import { FeatureGuard } from '@texas/components/shared/featureGuard/FeatureGuard';
import {
  Flex,
  TableContainer,
  Table,
  Thead,
  Text,
  Tr,
  Th,
  Tbody,
  Td,
  Checkbox,
  Box,
  SimpleGrid,
  Spinner,
} from '@chakra-ui/react';
import { CompositionGroup } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { CompositionGroupTitle } from '@texas/components/shared/compositionGroup/CompositionGroupTitle';
import { Icons } from '@texas/components/shared/Icons';
import { PaImage } from '@texas/components/shared/productApproval/PaImage';
import { evaluateSampleNextAction } from '@texas/components/shared/samples/evaluateSampleNextAction';
import { VersionCurrentState } from '@texas/components/shared/samples/VersionCurrentState';
import { SampleNextAction } from '@texas/components/shared/samples/SampleNextAction';
import { SampleProgress } from '@texas/components/shared/samples/SampleProgress';
import { MirroringTag } from '@texas/components/shared/suppliers/MirroringTag';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import React, { useMemo } from 'react';
import { TagWithTooltip } from '@texas/components/shared/TagWithTooltip';
import { useTranslation } from 'react-i18next';
import { CompositionGroupHistoryModal } from '../../history/CompositionGroupHistoryModal';
import {
  SampleAction,
  SampleLifecycle,
} from '@texas/components/shared/samples/types';
import { getRowId } from '@texas/components/shared/samples/helper';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { MultiSelectContext } from '../Versions';
import { VersionsMenu } from './VersionsMenu';
import { ColorDot } from '@texas/components/shared/colorPicker/ColorDot';
import { compareEnums } from '@texas/utils/helpers/enumHelpers';
import { ConfigureCompositionGroupGarp } from '@texas/messageBus/messages';
import { usePublisher } from '@texas/messageBus/useMessageBus';
import { useVersionOverviewContext } from './VersionOverviewContext';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';

export interface CopyProps {
  groupId: number;
  supplierId: number;
  branchId: number;
  basedOnId: number | null;
  cellX: number | null;
  cellY: number | null;
  replaceId: number | null;
}

export interface EmptyCell {
  xLabel: string;
  yLabel: string;
}

export function GroupedVersions({
  materialHeaders,
  header,
  versions,
  onRestoreOpen,
  onArchiveOpen,
  onOpen,
  onCopyOpen,
  versionsLoading,
  showCellColumns,
  emptyCells,
}: {
  materialHeaders: React.ReactNode;
  header: React.ReactNode;
  versions: CompositionGroup[];
  onRestoreOpen: (id: number) => void;
  onArchiveOpen: (id: number) => void;
  onOpen: (id: number, readOnly: boolean, isArchived: boolean) => void;
  onCopyOpen: (props: CopyProps) => void;
  versionsLoading: boolean;
  showCellColumns?: boolean;
  emptyCells?: EmptyCell[];
}) {
  const { t } = useTranslation();
  const { filters } = useVersionOverviewContext();
  const { publish } = usePublisher();

  const { selectedRows, setSelectedRows } =
    useActiveContext(MultiSelectContext);
  const {
    isOpen: isHistoryOpen,
    onClose: onHistoryClose,
    onOpen: onHistoryOpen,
    value: historyValue,
  } = useValueDisclosure<{
    id: number;
    name: string;
  }>();

  const filteredVersions = useMemo(() => {
    if (filters.showArchived) return versions;
    return versions.filter((v) => !v.archived);
  }, [filters.showArchived, versions]);

  return (
    <Flex flexDir="column" pt={6}>
      <Flex gap={4}>{header}</Flex>
      {versionsLoading && <Spinner mt={2} label={t('general.loading')} />}
      {!versionsLoading &&
        (filteredVersions.length === 0 && !emptyCells?.length ? (
          <Text variant="sub">{t('general.noData')}</Text>
        ) : (
          <TableContainer>
            <Table variant="versions-list-view">
              <Thead>
                <Tr>
                  <Th minW={6} />
                  <Th minW={12} />
                  <Th minW="24" />
                  <Th minW="40">{t('general.photo')}</Th>
                  <FeatureGuard hideBadge={true}>
                    <Th>{t('inquiry.inquiry')}</Th>
                  </FeatureGuard>
                  <Th>{t('suppliers.supplier')}</Th>
                  <Th>{t('general.branch')}</Th>
                  {showCellColumns && <Th />}
                  {showCellColumns && <Th />}
                  <Th>{t('productApproval.number')}</Th>
                  <Th>{t('general.option')}</Th>
                  <Th>{t('general.version')}</Th>
                  {materialHeaders}
                </Tr>
              </Thead>
              <Tbody>
                {filteredVersions.map((v) => {
                  const lifecycle = v.mainProductApproval?.mainSample
                    ? v.mainProductApproval.mainSample.lifecycle
                    : SampleLifecycle.Nothing;

                  const nextAction = evaluateSampleNextAction({
                    state: lifecycle,
                    sampleId: v.mainProductApproval?.mainSample?.id,
                    versionArchived: !!v.archived,
                  });

                  return (
                    <Tr
                      key={v.id}
                      role="group"
                      transition="opacity 200ms ease"
                      _hover={{ opacity: 0.7, cursor: 'pointer' }}
                      className={
                        !v.controlledById && v.matrixNodeXValue
                          ? 'unique'
                          : undefined
                      }
                    >
                      <Td>
                        <Checkbox
                          isChecked={selectedRows[v.id] === true}
                          onChange={(e) => {
                            setSelectedRows((r) => ({
                              ...r,
                              [getRowId(v.id)]: e.target.checked,
                            }));
                          }}
                        />
                      </Td>
                      <Td>
                        <SimpleGrid>
                          <Box
                            gridColumn={1}
                            gridRow={1}
                            opacity={1}
                            _groupHover={{ opacity: '0' }}
                            transition="opacity 200ms ease"
                          >
                            <SampleProgress
                              paState={v.mainProductApproval?.state}
                              state={lifecycle}
                            />
                          </Box>
                          <Flex
                            gridColumn={1}
                            gridRow={1}
                            gap={2}
                            align="center"
                            opacity="0"
                            _groupHover={{ opacity: '1' }}
                            margin="auto"
                            transition="opacity 200ms ease"
                          >
                            <VersionsMenu
                              version={v}
                              onRestoreOpen={onRestoreOpen}
                              onArchiveOpen={onArchiveOpen}
                              onOpen={(id, readOnly) =>
                                onOpen(id, readOnly, false)
                              }
                              onCopyOpen={onCopyOpen}
                              onHistoryOpen={(id, name) =>
                                onHistoryOpen({ id, name })
                              }
                              basedOnGroupVersion={v.basedOnGroupVersion ?? 0}
                              onSetGarpDataOpen={
                                v.linkedVariantId && v.branchId
                                  ? () =>
                                      publish(
                                        new ConfigureCompositionGroupGarp(
                                          v.id,
                                          v.linkedVariantId!,
                                          v.branchId!,
                                          v.mainProductApproval?.productApprovalId,
                                        ),
                                      )
                                  : undefined
                              }
                            />
                          </Flex>
                        </SimpleGrid>
                      </Td>

                      <Td>
                        <Flex
                          alignItems="center"
                          justifyContent="end"
                          gap={2}
                          px={2}
                        >
                          <VersionCurrentState
                            archived={v.archived}
                            lifecycle={lifecycle}
                            paState={v.mainProductApproval?.state}
                            sampleReadyDate={
                              v.mainProductApproval?.mainSample
                                ?.sampleReadyDate ?? null
                            }
                          />
                          {!compareEnums(SampleAction, nextAction, [
                            SampleAction.None,
                            SampleAction.InitiateSampling,
                          ]) && <Icons.ChevronRight />}
                          {!v.archived && (
                            <SampleNextAction
                              action={nextAction}
                              lifecycle={lifecycle}
                              transportRequestId={
                                v.mainProductApproval?.mainSample?.transport?.id
                              }
                              sampleId={v.mainProductApproval?.mainSample?.id}
                              groupId={v.id}
                              pa={
                                v.mainProductApproval
                                  ? {
                                      id: v.mainProductApproval
                                        .productApprovalId,
                                      state: v.mainProductApproval.state,
                                    }
                                  : undefined
                              }
                              variantId={v.variantId}
                              supplierId={v.supplierId!}
                              articleFolderId={v.folderId}
                              branchId={v.branchId!}
                              shipmentId={
                                v.mainProductApproval?.mainSample?.transport
                                  ?.shipment?.id
                              }
                            />
                          )}
                        </Flex>
                      </Td>
                      <Td>
                        {v.mainProductApproval && (
                          <PaImage
                            fileIdentifier={
                              v.mainProductApproval.fileIdentifier
                            }
                            fileId={v.mainProductApproval.fileId}
                            paId={v.mainProductApproval.productApprovalId}
                            paNumber={v.mainProductApproval.formatNumber}
                            itemCode={v.articleItemCode}
                            articleFolderId={v.folderId}
                            matrixNodeXValue={v.matrixNodeXValue}
                            matrixNodeYValue={v.matrixNodeYValue}
                            maxW="64px"
                            maxH="64px"
                          />
                        )}
                      </Td>
                      <FeatureGuard hideBadge={true}>
                        <Td>
                          {v.inquiryId && v.formattedInquiryNumber ? (
                            <AngularNavLink
                              to="variant.inquiry.detail"
                              navobj={{ inquiryId: v.inquiryId }}
                            >
                              {v.formattedInquiryNumber}
                            </AngularNavLink>
                          ) : (
                            <Text color="gray.500">-</Text>
                          )}
                        </Td>
                      </FeatureGuard>
                      <Td>
                        <Text w="60" whiteSpace="normal">
                          {v.supplierName}
                        </Text>
                        {!!v.supplierTags?.length && (
                          <Flex gap={1} wrap="wrap" mt={1}>
                            {v.supplierTags.map((tag) => (
                              <TagWithTooltip key={tag.id} tag={tag} />
                            ))}
                          </Flex>
                        )}
                        {v.followers && (
                          <MirroringTag
                            followers={v.followers}
                            showCount={true}
                          />
                        )}
                      </Td>
                      <Td>
                        <Text>{v.branchIdentifier}</Text>
                      </Td>
                      {showCellColumns && (
                        <Td>
                          <Text>{v.matrixNodeXValue}</Text>
                        </Td>
                      )}
                      {showCellColumns && (
                        <Td>
                          <Text>{v.matrixNodeYValue}</Text>
                        </Td>
                      )}
                      <Td>
                        {v.mainProductApproval && (
                          <Text>{v.mainProductApproval.formatNumber}</Text>
                        )}
                      </Td>
                      {v.basedOnGroupVersion ? (
                        <Td>
                          <CompositionGroupTitle
                            version={v.basedOnGroupVersion}
                            note={v.basedOnGroupName}
                            type="option"
                            variant="none"
                          />
                        </Td>
                      ) : (
                        <Td />
                      )}
                      <Td>
                        {t('compositionGroup.version.versionNr', {
                          nr: v.version,
                        })}
                      </Td>
                      {v.compositions.map((c) => {
                        return (
                          <React.Fragment key={c.id}>
                            {c.materials.map((m) => {
                              return (
                                <React.Fragment key={m.id}>
                                  <Td>{m.material.text}</Td>
                                  <Td>
                                    {m.qualities.map((q) => {
                                      return (
                                        <React.Fragment key={q.id}>
                                          <Text>- {q.text}</Text>
                                        </React.Fragment>
                                      );
                                    })}
                                  </Td>
                                  <Td>
                                    {m.qualities.map((q) => {
                                      return (
                                        <React.Fragment key={q.id}>
                                          <Text>{q.value}</Text>
                                        </React.Fragment>
                                      );
                                    })}
                                  </Td>
                                  <Td>
                                    {m.treatments.map((t) => {
                                      return (
                                        <React.Fragment key={t.id}>
                                          <Text>- {t.text}</Text>
                                        </React.Fragment>
                                      );
                                    })}
                                  </Td>
                                  <Td>
                                    {m.techniques.map((t) => {
                                      return (
                                        <React.Fragment key={t.id}>
                                          <Text>- {t.text}</Text>
                                        </React.Fragment>
                                      );
                                    })}
                                  </Td>
                                  <Td>
                                    <Flex gap={1} flexWrap="wrap">
                                      {m.colors.map((c, i) => {
                                        return (
                                          <React.Fragment key={i}>
                                            <ColorDot
                                              key={i}
                                              hex={c.hex}
                                              code={c.code}
                                              name={c.name}
                                            />
                                          </React.Fragment>
                                        );
                                      })}
                                    </Flex>
                                  </Td>
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </Tr>
                  );
                })}
                {emptyCells?.map(({ xLabel, yLabel }) => (
                  <Tr
                    key={`empty-${xLabel}-${yLabel}`}
                    opacity={0.4}
                    borderStyle="dashed"
                  >
                    <Td>
                      <Text>{t('general.empty')}</Text>
                    </Td>
                    <Td />
                    <Td />
                    <Td />
                    <FeatureGuard hideBadge={true}>
                      <Td />
                    </FeatureGuard>
                    <Td />
                    <Td />
                    {showCellColumns && (
                      <Td>
                        <Text>{xLabel}</Text>
                      </Td>
                    )}
                    {showCellColumns && (
                      <Td>
                        <Text>{yLabel}</Text>
                      </Td>
                    )}
                    <Td />
                    <Td />
                    <Td />
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        ))}

      {historyValue && (
        <CompositionGroupHistoryModal
          isOpen={isHistoryOpen}
          onClose={onHistoryClose}
          compositionGroupId={historyValue.id}
          name={historyValue.name}
        />
      )}
    </Flex>
  );
}
